import type Phaser from 'phaser';

export interface NarrationCardOptions {
  scene: Phaser.Scene;
  /** Center anchor for the card. Updated via setCenter when anchor flips. */
  cx: number;
  cy: number;
  width: number;
  height: number;
  dpr: number;
  onBack: () => void;
  onNext: () => void;
  onSkip: () => void;
  /** Click handler when the card is in finish mode (last step). */
  onFinish: () => void;
}

const CARD_BG_TOP = 0x0d2c1d;
const CARD_BG_BOT = 0x06140e;
const GOLD = 0xd4af37;

export class NarrationCard {
  private readonly scene: Phaser.Scene;
  private readonly dpr: number;
  private readonly width: number;
  private readonly height: number;
  private cx: number;
  private cy: number;
  private bg!: Phaser.GameObjects.Graphics;
  private counterText!: Phaser.GameObjects.Text;
  private skipText!: Phaser.GameObjects.Text;
  private skipHit!: Phaser.GameObjects.Rectangle;
  private titleText!: Phaser.GameObjects.Text;
  private bodyText!: Phaser.GameObjects.Text;
  private backText!: Phaser.GameObjects.Text;
  private backHit!: Phaser.GameObjects.Rectangle;
  private nextBg!: Phaser.GameObjects.Graphics;
  private nextText!: Phaser.GameObjects.Text;
  private nextHit!: Phaser.GameObjects.Rectangle;
  private requireHintText!: Phaser.GameObjects.Text;
  private finishMode = false;

  constructor(opts: NarrationCardOptions) {
    this.scene = opts.scene;
    this.dpr = opts.dpr;
    this.width = opts.width;
    this.height = opts.height;
    this.cx = opts.cx;
    this.cy = opts.cy;
    this.draw(opts.onBack, opts.onNext, opts.onSkip, opts.onFinish);
  }

  setCenter(cx: number, cy: number) {
    this.cx = cx;
    this.cy = cy;
    this.relayout();
  }

  setStep(opts: {
    index: number;
    total: number;
    title: string | undefined;
    body: string;
    backEnabled: boolean;
    nextEnabled: boolean;
    requirementHint: string | undefined;
    /** When true, the right-side button reads FINISH and routes to onFinish. */
    finish?: boolean;
  }) {
    this.counterText.setText(`${opts.index + 1} / ${opts.total}`);
    this.titleText.setText(opts.title ?? '');
    this.titleText.setVisible(!!opts.title);
    this.bodyText.setText(opts.body);
    this.backText.setAlpha(opts.backEnabled ? 1 : 0.35);
    this.backHit.setInteractive(opts.backEnabled);
    this.finishMode = !!opts.finish;
    this.nextText.setText(this.finishMode ? 'FINISH' : 'NEXT ▶');
    this.setNextEnabled(this.finishMode || opts.nextEnabled);
    this.requireHintText.setText(opts.requirementHint ?? '');
    this.requireHintText.setVisible(!!opts.requirementHint);
    this.relayout();
  }

  setNextEnabled(enabled: boolean) {
    this.nextText.setAlpha(enabled ? 1 : 0.4);
    this.nextBg.setAlpha(enabled ? 1 : 0.4);
    if (enabled) this.nextHit.setInteractive({ useHandCursor: true });
    else this.nextHit.disableInteractive();
  }

  setVisible(v: boolean) {
    const owned = [
      this.bg,
      this.counterText,
      this.skipText,
      this.skipHit,
      this.titleText,
      this.bodyText,
      this.backText,
      this.backHit,
      this.nextBg,
      this.nextText,
      this.nextHit,
      this.requireHintText,
    ];
    for (const o of owned) o.setVisible(v);
  }

  destroy() {
    const owned: Phaser.GameObjects.GameObject[] = [
      this.bg,
      this.counterText,
      this.skipText,
      this.skipHit,
      this.titleText,
      this.bodyText,
      this.backText,
      this.backHit,
      this.nextBg,
      this.nextText,
      this.nextHit,
      this.requireHintText,
    ];
    for (const o of owned) o.destroy();
  }

  private px(n: number) {
    return Math.round(n * this.dpr);
  }

  private draw(onBack: () => void, onNext: () => void, onSkip: () => void, onFinish: () => void) {
    const fontFamily = '"Helvetica Neue", Helvetica, Arial, sans-serif';

    this.bg = this.scene.add.graphics();

    this.counterText = this.scene.add
      .text(0, 0, '', {
        fontFamily,
        fontSize: `${this.px(11)}px`,
        color: '#a8b3a8',
      })
      .setOrigin(0, 0);

    this.skipText = this.scene.add
      .text(0, 0, 'SKIP TUTORIAL', {
        fontFamily,
        fontSize: `${this.px(11)}px`,
        color: '#a8b3a8',
        fontStyle: 'bold',
      })
      .setOrigin(1, 0);

    this.skipHit = this.scene.add
      .rectangle(0, 0, this.px(120), this.px(20), 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    this.skipHit.on('pointerdown', onSkip);
    this.skipHit.on('pointerover', () => this.skipText.setColor('#f4d35e'));
    this.skipHit.on('pointerout', () => this.skipText.setColor('#a8b3a8'));

    this.titleText = this.scene.add
      .text(0, 0, '', {
        fontFamily,
        fontSize: `${this.px(15)}px`,
        color: '#f4d35e',
        fontStyle: 'bold',
        wordWrap: { width: this.width - this.px(32) },
      })
      .setOrigin(0, 0);

    this.bodyText = this.scene.add
      .text(0, 0, '', {
        fontFamily,
        fontSize: `${this.px(13)}px`,
        color: '#e6e6dc',
        wordWrap: { width: this.width - this.px(32) },
      })
      .setOrigin(0, 0);

    this.backText = this.scene.add
      .text(0, 0, '◀ BACK', {
        fontFamily,
        fontSize: `${this.px(12)}px`,
        color: '#a8b3a8',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);
    this.backHit = this.scene.add
      .rectangle(0, 0, this.px(80), this.px(36), 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    this.backHit.on('pointerdown', onBack);

    this.nextBg = this.scene.add.graphics();
    this.nextText = this.scene.add
      .text(0, 0, 'NEXT ▶', {
        fontFamily,
        fontSize: `${this.px(13)}px`,
        color: '#0d2c1d',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.nextHit = this.scene.add
      .rectangle(0, 0, this.px(96), this.px(36), 0x000000, 0)
      .setInteractive({ useHandCursor: true });
    this.nextHit.on('pointerdown', () => {
      if (this.finishMode) onFinish();
      else onNext();
    });

    this.requireHintText = this.scene.add
      .text(0, 0, '', {
        fontFamily,
        fontSize: `${this.px(12)}px`,
        color: '#cfc28a',
        fontStyle: 'italic',
      })
      .setOrigin(0.5);
  }

  private relayout() {
    const padX = this.px(16);
    const padY = this.px(14);
    const x0 = this.cx - this.width / 2;
    const y0 = this.cy - this.height / 2;

    this.bg.clear();
    this.bg.fillGradientStyle(CARD_BG_TOP, CARD_BG_TOP, CARD_BG_BOT, CARD_BG_BOT, 1);
    this.bg.fillRoundedRect(x0, y0, this.width, this.height, this.px(10));
    this.bg.lineStyle(this.px(1), GOLD, 1);
    this.bg.beginPath();
    this.bg.moveTo(x0, y0);
    this.bg.lineTo(x0 + this.width, y0);
    this.bg.strokePath();

    this.counterText.setPosition(x0 + padX, y0 + padY);
    this.skipText.setPosition(x0 + this.width - padX, y0 + padY);
    this.skipHit.setPosition(x0 + this.width - padX - this.px(60), y0 + padY + this.px(8));

    const headerBottom = y0 + padY + this.counterText.height + this.px(8);
    if (this.titleText.visible) {
      this.titleText.setPosition(x0 + padX, headerBottom);
    }
    const bodyY = this.titleText.visible
      ? this.titleText.y + this.titleText.height + this.px(6)
      : headerBottom;
    this.bodyText.setPosition(x0 + padX, bodyY);

    const buttonRowY = y0 + this.height - padY - this.px(18);
    this.backText.setPosition(x0 + padX, buttonRowY);
    this.backHit.setPosition(x0 + padX + this.px(40), buttonRowY);

    const nextW = this.px(96);
    const nextH = this.px(36);
    const nextX = x0 + this.width - padX - nextW;
    const nextY = buttonRowY - this.px(18);
    this.nextBg.clear();
    this.nextBg.fillStyle(GOLD, 1);
    this.nextBg.fillRoundedRect(nextX, nextY, nextW, nextH, this.px(8));
    this.nextText.setPosition(nextX + nextW / 2, nextY + nextH / 2);
    this.nextHit.setPosition(nextX + nextW / 2, nextY + nextH / 2);

    this.requireHintText.setPosition(this.cx, buttonRowY);
  }
}
